import React, { useState } from 'react';
import { FormHelperText, IconButton, TextField } from '@mui/material';
import { Topic } from '@shared/domain/topic/topic.aggregate';

import { Control, Controller, ControllerRenderProps } from 'react-hook-form';
import PictureInPictureAltIcon from '@mui/icons-material/PictureInPictureAlt';
import { SubtopicModal } from 'subtopic/subtopicModal';
import { ClickableCanvas } from 'subtopic/clickableCanvas';
import { validations } from '@shared/infrastructure/ui/validations';
import InputLabel from '@mui/material/InputLabel';
import { Coordinates } from '@shared/domain/subtopic/subtopic.aggregate';

import ClinicImgGeneric from 'assets/images/clinic/clinicoverview_generic.png';
import ClinicImgCDK from 'assets/images/clinic/clinicoverview_cdk.png';
import ClinicImgCriticalCare from 'assets/images/clinic/clinicoverview_criticalcare.png';
import ClinicImgHaemodialysis from 'assets/images/clinic/clinicoverview_haemodialysis.png';
import ClinicImgHomedialysis from 'assets/images/clinic/clinicoverview_homedialysis.png';

export const TopicInfo = ({
  topic,
  therapyName,
  control,
  disableName
}: {
  topic?: Topic;
  therapyName?: string;
  control: Control<Topic, any>;
  disableName?: boolean;
}) => {
  const [positionModal, setPositionModal] = useState<{
    isOpen: boolean;
    field?: ControllerRenderProps<Topic, 'position'>;
  }>({
    isOpen: false
  });

  function getClinicImageByFloor() {
    switch (therapyName) {
      case 'ckd':
        return ClinicImgCDK;
      case 'homeDialysis':
        return ClinicImgHomedialysis;
      case 'hemodialysis':
        return ClinicImgHaemodialysis;
      case 'criticalCare':
        return ClinicImgCriticalCare;
      default:
        return ClinicImgGeneric;
    }
  }

  return (
    <>
      {positionModal.isOpen && (
        <SubtopicModal
          isOpen={positionModal.isOpen}
          width='1000px'
          height='500px'
          onClose={() => {
            positionModal.field!.onBlur();
            setPositionModal({ isOpen: false });
          }}
        >
          <ClickableCanvas
            position={{
              x: positionModal.field!.value?.x / 2,
              y: positionModal.field!.value?.y / 2
            }}
            onClick={(position: Coordinates) => {
              positionModal.field!.onBlur();
              positionModal.field!.onChange({
                x: position.x * 2,
                y: position.y * 2
              });
            }}
            background={getClinicImageByFloor()}
          ></ClickableCanvas>
        </SubtopicModal>
      )}

      <Controller
        name='name'
        control={control}
        rules={{
          ...validations.required(),
          ...validations.pattern(
            /^[a-z]+(-[a-z]+)*$/,
            'This value must be composed of groups of lowercase letters, separated by hypens (-).'
          )
        }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={{ my: '0.3rem' }}
            label='Identifier'
            placeholder='example-url'
            disabled={disableName}
          />
        )}
      />
      <Controller
        name='nameLanguage'
        control={control}
        rules={validations.notWhiteSpace()}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={{ my: '0.3rem' }}
            label='Title'
          />
        )}
      />
      <Controller
        name='position'
        control={control}
        rules={validations.required(
          'Select a position by clicking on the icon above.'
        )}
        render={({ field, fieldState }) => (
          <>
            <InputLabel error={!!fieldState.error}>
              Edit Topic Position
              <IconButton
                size='large'
                color='inherit'
                aria-label='open tab button'
                ref={field.ref}
                onClick={() => {
                  field.onBlur();
                  setPositionModal({ isOpen: true, field });
                }}
              >
                <PictureInPictureAltIcon></PictureInPictureAltIcon>
              </IconButton>
            </InputLabel>
            <FormHelperText
              sx={{ marginRight: '14px', marginLeft: '14px' }}
              error={!!fieldState.error}
            >
              {fieldState.error?.message}
            </FormHelperText>
          </>
        )}
      />
    </>
  );
};
